import React, { useState, useEffect } from "react";
import { useGLTF } from "@react-three/drei";
import { useSnapshot } from "valtio";

const FINISH_PRESETS = {
  plain: { metalness: 0, roughness: 0.5 },
  matte: { metalness: 0, roughness: 1 },
  metallic: { metalness: 0.55, roughness: 0.3 },
  fluorescent: { metalness: 0, roughness: 0.4, emissiveIntensity: 0.5 },
};

export default function BoxingX1(props) {
  const { nodes } = useGLTF("/boxing-x1/boxing-x1.gltf");
  const snap = useSnapshot(props.colors);
  const finishSnap = useSnapshot(props.finishes || {});
  const [hovered, setHovered] = useState(null);

  const finishProps = (key) => {
    const preset = FINISH_PRESETS[finishSnap[key]] || FINISH_PRESETS.plain;
    return preset.emissiveIntensity
      ? { ...preset, emissive: snap[key] }
      : preset;
  };

  useEffect(() => {
    const cursor = `<svg width="64" height="64" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0)"><path fill="rgba(255, 255, 255, 0.5)" d="M29.5 54C43.031 54 54 43.031 54 29.5S43.031 5 29.5 5 5 15.969 5 29.5 15.969 54 29.5 54z" stroke="#000"/><g filter="url(#filter0_d)"><path d="M29.5 47C39.165 47 47 39.165 47 29.5S39.165 12 29.5 12 12 19.835 12 29.5 19.835 47 29.5 47z" fill="${snap[hovered]}"/></g><path d="M2 2l11 2.947L4.947 13 2 2z" fill="#000"/><text fill="#000" style="white-space:pre" font-family="Inter var, sans-serif" font-size="10" letter-spacing="-.01em"><tspan x="35" y="63">${hovered}</tspan></text></g><defs><clipPath id="clip0"><path fill="#fff" d="M0 0h64v64H0z"/></clipPath><filter id="filter0_d" x="6" y="8" width="47" height="47" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feColorMatrix in="SourceAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"/><feOffset dy="2"/><feGaussianBlur stdDeviation="3"/><feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0"/><feBlend in2="BackgroundImageFix" result="effect1_dropShadow"/><feBlend in="SourceGraphic" in2="effect1_dropShadow" result="shape"/></filter></defs></svg>`;
    if (hovered) {
      document.body.style.cursor = `url('data:image/svg+xml;base64,${btoa(
        cursor
      )}'), auto`;
    }
    return () => (document.body.style.cursor = "auto");
  }, [hovered, snap]);

  return (
    <group
      {...props}
      dispose={null}
      scale={[0.4, 0.4, 0.4]}
      position={[-0.02, 0.14, 0.08]}
      onPointerOver={(e) => {
        e.stopPropagation();
        setHovered(e.object.material.name);
      }}
      onPointerOut={(e) => {
        if (e.intersections.length === 0) {
          setHovered(null);
        }
      }}
      onPointerDown={(e) => {
        e.stopPropagation();
        props.updateCurrent(e.object.material.name);
      }}
      onPointerMissed={() => {
        props.updateCurrent(null);
      }}
    >
      <mesh castShadow geometry={nodes.black.geometry} position={[0.00356, -0.5704, 0.13691]}>
        <meshStandardMaterial color={snap.black} name="black" {...finishProps("black")} />
      </mesh>
      <mesh castShadow geometry={nodes.wristback.geometry} position={[-0.00004, 0.14143, 0.17259]}>
        <meshStandardMaterial color={snap.wristback} name="wristback" {...finishProps("wristback")} />
      </mesh>
      <mesh castShadow geometry={nodes.piping.geometry} position={[0.00163, -0.60051, 0.09996]}>
        <meshStandardMaterial color={snap.piping} name="piping" {...finishProps("piping")} />
      </mesh>
      <mesh
        castShadow
        geometry={nodes.strap.geometry}
        position={[0.68867, -1.02814, 0.06587]}
        quaternion={[0, 0, -0.70711, 0.70711]}
      >
        <meshStandardMaterial color={snap.strap} name="strap" {...finishProps("strap")} />
      </mesh>
      <mesh castShadow geometry={nodes.tstrip.geometry} position={[0, 0, 0.20918]}>
        <meshStandardMaterial color={snap.tstrip} name="tstrip" {...finishProps("tstrip")} />
      </mesh>
      <mesh castShadow geometry={nodes.palm.geometry} position={[-0.00004, 0.09714, 0.17259]}>
        <meshStandardMaterial color={snap.palm} name="palm" {...finishProps("palm")} />
      </mesh>
      <mesh castShadow geometry={nodes.connect.geometry} position={[-0.62127, 1.29494, -0.27941]}>
        <meshStandardMaterial color={snap.connect} name="connect" {...finishProps("connect")} />
      </mesh>
      <mesh castShadow geometry={nodes.thumbin.geometry} position={[-0.42637, 0.59328, -0.19952]}>
        <meshStandardMaterial color={snap.thumbin} name="thumbin" {...finishProps("thumbin")} />
      </mesh>
      <mesh castShadow geometry={nodes.velcro.geometry} position={[0.0001, -1.0166, 0.27232]}>
        <meshStandardMaterial color={snap.velcro} name="velcro" {...finishProps("velcro")} />
      </mesh>
      <mesh castShadow geometry={nodes.thumbout.geometry} position={[-0.79927, 0.32749, 0.17259]}>
        <meshStandardMaterial color={snap.thumbout} name="thumbout" {...finishProps("thumbout")} />
      </mesh>
      <mesh castShadow geometry={nodes.palmback.geometry} position={[-0.00004, 0.14143, 0.17259]}>
        <meshStandardMaterial color={snap.palmback} name="palmback" {...finishProps("palmback")} />
      </mesh>
      <mesh castShadow geometry={nodes.stitches.geometry}>
        <meshStandardMaterial color={snap.stitches} name="stitches" {...finishProps("stitches")} />
      </mesh>
      <mesh castShadow geometry={nodes.p1.geometry} position={[-0.04696, 0.65974, -0.19219]} scale={[1.005, 1.005, 1.005]}>
        <meshStandardMaterial color={snap.p1} name="p1" {...finishProps("p1")} />
      </mesh>
      <mesh castShadow geometry={nodes.p2.geometry}>
        <meshStandardMaterial color={snap.p2} name="p2" {...finishProps("p2")} />
      </mesh>
      <mesh castShadow geometry={nodes.p3.geometry}>
        <meshStandardMaterial color={snap.strap} name="strap" {...finishProps("strap")} />
      </mesh>
      <mesh castShadow geometry={nodes.p4.geometry}>
        <meshStandardMaterial color={snap.p4} name="p4" {...finishProps("p4")} />
      </mesh>
      <mesh castShadow geometry={nodes.p5.geometry} position={[-0.04696, 0.65974, -0.19219]} scale={[1.005, 1.005, 1.005]}>
        <meshStandardMaterial color={snap.palmback} name="palmback" {...finishProps("palmback")} />
      </mesh>
      <mesh castShadow geometry={nodes.p6.geometry}>
        <meshStandardMaterial color={snap.strap} name="strap" {...finishProps("strap")} />
      </mesh>
    </group>
  );
}

useGLTF.preload("/boxing-x1/boxing-x1.gltf");
